<script setup lang="ts">
import CardHeading from "../CardHeading.vue";
import { computed, ref, watch } from "vue";
import { Country_Enum } from "@/graphql";

import { WizardProjectType } from "@/features/BuildingProject";
import { useCalculationTypes, CalculationTypesType } from "@/dataProviders/CalculationTypes";
import type { CalculationType } from "@/graphql";
import { useIndicators, IndicatorTypeType, DenominatorUnitType } from "@/dataProviders/Indicators";

import NumberInput from "@/components/Reusables/Inputs/NumberInput.vue";
import InputLabel from "@/components/Reusables/Inputs/InputLabel.vue";
import SelectInputWithKeyValue from "@/components/Reusables/Inputs/SelectInputWithKeyValue.vue";
import { useWizardProject } from "@/features/BuildingProject/composables/useWizardProject";
import { calculationTypeDefaults } from "@/features/BuildingProject/utils/calculationTypeDefaults";
import {
  isCalculationTypeListAuthoritative,
  resolveCalculationTypeForCountry,
} from "@/features/BuildingProject/utils/resolveCalculationTypeForCountry";
import { countryLongNameToEnumMap } from "@/providers/Localization";
import { suppressThresholdFallback } from "@/features/BuildingProject/utils/thresholdFallback";
import { applyResolvedLimit } from "@/features/BuildingProject/utils/resolvedLimit";
import { resolveWeightedLimitValue } from "@/features/BuildingProject/utils/calculationLimitValue";
import { useToast } from "vue-toastification";

const props = withDefaults(
  defineProps<{
    wasValidated: boolean;
    project: WizardProjectType;
    /**
     * Caps the field grid at two columns instead of three. CalculationPrerequisites.vue never passes this,
     * so its full-width step keeps today's three-up layout unchanged. ProjectDetails' Calculation card sets
     * it: that column is roughly a third the width the three-up layout was built for, and InputLabel's
     * fixed-height, non-wrapping label (see InputLabel.vue) overflows into the next field instead of
     * wrapping when a column gets that narrow, measured as "Limit value (kgCO₂-eq/m²/year)*" and
     * "Consideration period (years)" visibly overlapping.
     */
    compact?: boolean;
    /**
     * Hides the "Calculation type" heading. Off by default so CalculationPrerequisites.vue's step, which
     * has nothing else naming this section, is unaffected. ProjectDetails' Calculation card sets it false:
     * the card's own header already says "Calculation", and a second, near-identical heading directly under
     * it read as a mistake rather than two screens sharing a component.
     */
    showHeading?: boolean;
  }>(),
  { compact: false, showHeading: true }
);

const calculationTypes = useCalculationTypes();
const { getThresholds } = useCalculationTypes();

const indicators = useIndicators();
const toast = useToast();

const { project, isExistingProject, lens } = useWizardProject(() => props.project);

// The five fields choosing a calculation type writes. Named here so the write below reads as an assignment
// rather than six paths into the object graph.
const considerationPeriodField = lens(
  (current) => current.emissionCalculationAssumptions.calculationType.considerationPeriod,
  (current, value) => (current.emissionCalculationAssumptions.calculationType.considerationPeriod = value)
);
const thresholdLimitWithoutExtra = lens(
  (current) => current.emissionCalculationAssumptions.calculationType.thresholdLimitWithoutExtra,
  (current, value) => (current.emissionCalculationAssumptions.calculationType.thresholdLimitWithoutExtra = value)
);
const includedPhases = lens(
  (current) => current.emissionCalculationAssumptions.emissionMetaData.includedPhases,
  (current, value) => (current.emissionCalculationAssumptions.emissionMetaData.includedPhases = value)
);
const includedPhasesTranslated = lens(
  (current) => current.emissionCalculationAssumptions.emissionMetaData.includedPhasesTranslated,
  (current, value) => (current.emissionCalculationAssumptions.emissionMetaData.includedPhasesTranslated = value)
);
const calculationTypeEnum = lens(
  (current) => current.emissionCalculationAssumptions.calculationType.calculationTypeEnum,
  (current, value) => (current.emissionCalculationAssumptions.calculationType.calculationTypeEnum = value)
);
const installationThresholdLimit = lens(
  (current) => current.emissionCalculationAssumptions.calculationType.installationThresholdLimit,
  (current, value) => (current.emissionCalculationAssumptions.calculationType.installationThresholdLimit = value)
);
// Whether the project's limit value was resolved from a building-type row by this form (see applyResolvedLimit).
const thresholdFromRow = ref(false);

const selectedCalculationType = ref<CalculationTypesType[0] | CalculationType | null>();

const calculationTypeOptions = computed(() =>
  calculationTypes.getCalculationTypes().map((calculationType) => ({ name: calculationType.label, value: calculationType.label }))
);

const handleCalculationTypeSelected = (label: string) => {
  const chosen = calculationTypes.getCalculationTypes().find((calculationType) => calculationType.label === label);
  if (!chosen) return;
  selectedCalculationType.value = chosen;
  handleNewSelectedCalculationType();
};

const handleNewSelectedCalculationType = () => {
  if (!selectedCalculationType.value) return;
  if (showConstruction.value || hasBuildingTypeThresholds.value) {
    updateThresholds();
  }

  // Shared with the modal that opens the wizard, so both agree on which phases a calculation type
  // includes — and therefore on how many steps the flow has.
  const defaults = calculationTypeDefaults(selectedCalculationType.value);

  // Update project. The two `??` keep the project's current value where the calculation type states
  // none, which is why the helper returns null there rather than deciding for us.
  considerationPeriodField.value = defaults.considerationPeriod ?? considerationPeriodField.value;
  thresholdLimitWithoutExtra.value = defaults.thresholdLimitWithoutExtra ?? thresholdLimitWithoutExtra.value;
  includedPhases.value = defaults.includedPhases;
  includedPhasesTranslated.value = defaults.includedPhasesTranslated;
  calculationTypeEnum.value = defaults.calculationTypeEnum;
};

const showConstruction = computed(() => {
  return (
    project.value.buildingAddress.country.toUpperCase() === Country_Enum.DENMARK &&
    ["BR18 (2025)", "Low emission (2025)"].includes(selectedCalculationType?.value?.label || "")
  );
});

const hasBuildingTypeThresholds = computed(() => {
  const entries = selectedCalculationType.value?.thresholdByBuildingType;
  return Array.isArray(entries) && entries.length > 0;
});

/**
 * Shared by every field in the grid below, so compact mode changes column count in exactly one place.
 *
 * Compact sizes by the CARD, not the viewport: the embedding card is a container (ProjectDetails marks
 * it `@container`), because rails and sidebars make viewport width a bad proxy for how much room the
 * card actually has — measured at a 1280px viewport, viewport-based `lg` put three fields in a ~660px
 * card and the nowrap labels overflowed the box. Two-up starts at a medium card (@lg, 512px); anything
 * narrower stacks.
 *
 * Three- and four-up used to need a much wider card than this (@3xl, 768px, and four-up was avoided
 * entirely) because InputLabel's nowrap label spilled into the next field rather than wrapping when a
 * column ran out of room. InputLabel now truncates its own label instead (InputLabel.vue) — a narrower
 * column clips its own text rather than overlapping its neighbour's — which is what makes both safe at
 * a lower threshold: three fields fit from @2xl (672px), and Construction limit joining as a fourth
 * needs @3xl (768px) rather than wrapping to a lonely row of its own.
 *
 * `min-w-0`: a grid item's default `min-width` is `auto`, which is its CONTENT's width, not its
 * assigned column's — so without this, a label wide enough to need truncating stopped the column
 * shrinking to fit it in the first place, and the whole row overflowed the card instead of clipping
 * the one label that needed it.
 */
const fieldSpanClass = computed(() => {
  if (!props.compact) return "col-span-12 sm:col-span-6 md:col-span-4 min-w-0";
  return showConstruction.value
    ? "col-span-12 @lg:col-span-6 @3xl:col-span-3 min-w-0"
    : "col-span-12 @lg:col-span-6 @2xl:col-span-4 min-w-0";
});

const considerationPeriodLabel = computed(() => {
  if (!selectedCalculationType.value) return "Consideration period";
  return `Consideration period (years)`;
});

// "Limit value", not "Threshold limit" (RTLCA create-wizard audit, finding F2): the number this labels
// is the SAME one the start-project dialog and the dashboard call the limit value, so this was the one
// place still using the old name for it.
const thresholdLimitLabel = computed(() => {
  if (!selectedCalculationType.value) return "Limit value";
  return `Limit value (${indicators.getIndicatorUnit(
    selectedCalculationType.value.thresholdIndicator.indicatorType as IndicatorTypeType,
    selectedCalculationType.value.thresholdIndicator.denominatorUnit as DenominatorUnitType
  )})`;
});

const totalArea = computed(() => {
  return (
    (project.value.emissionCalculationAssumptions.referencedAreas.areaAboveGround || 0) +
    (project.value.emissionCalculationAssumptions.referencedAreas.areaBelowGround || 0)
  );
});

const selectedThreshold = computed(() => {
  const calcType = selectedCalculationType.value;
  const buildingTypeId = Number(project.value.buildingTypeEnum);

  if (
    !calcType ||
    !calcType.thresholdByBuildingType ||
    !Array.isArray(calcType.thresholdByBuildingType) ||
    Number.isNaN(buildingTypeId)
  ) {
    return calcType?.threshold ?? null;
  }

  const match = calcType.thresholdByBuildingType.find((entry) => Number(entry.buildingTypeId) === buildingTypeId);

  return match?.threshold ?? calcType.threshold ?? null;
});

const selectedInstallationalThreshold = computed(() => {
  const calcType = selectedCalculationType.value;
  const buildingTypeId = Number(project.value.buildingTypeEnum);
  const area = totalArea.value;

  if (
    !calcType ||
    !calcType.thresholdByBuildingType ||
    !Array.isArray(calcType.thresholdByBuildingType) ||
    Number.isNaN(buildingTypeId)
  ) {
    return null;
  }

  // Find match based on building type and area, similar to getThresholds logic
  let match = calcType.thresholdByBuildingType.find(
    (entry) => Number(entry.buildingTypeId) === buildingTypeId && area <= (entry.maxTotalArea ?? Infinity)
  );

  // If no threshold is found for the specific building type and total area, look for a row where maxTotalArea is falsey
  if (match === undefined) {
    match = calcType.thresholdByBuildingType.find(
      (entry) => Number(entry.buildingTypeId) === buildingTypeId && !entry.maxTotalArea
    );
  }

  return match?.installationThreshold ?? null;
});

// The row's figure is shown when it has one; only a building type without one stores the project's own.
const constructionLimit = computed<number | null>({
  get: () => selectedInstallationalThreshold.value ?? installationThresholdLimit.value,
  set: (value) => (installationThresholdLimit.value = value),
});

const updateThresholds = async () => {
  const calcType = selectedCalculationType.value;
  const area = totalArea.value;
  const buildingTypeId = project.value.buildingTypeEnum;

  if (!calcType || buildingTypeId == null) return;

  // Shared with the start-project dialog, which shows this same limit value before the wizard opens.
  const noFallback = suppressThresholdFallback(project.value.buildingAddress.country, selectedCalculationType?.value?.label);

  const rowThresholds = await getThresholds(calcType as CalculationType, Number(buildingTypeId), area, noFallback);
  // A mixed-use building under BR18 (2025) takes the area-weighted limit; the A4-A5 figure stays the main use's.
  const weighted = resolveWeightedLimitValue(calcType, project.value.buildingUses, getThresholds);
  const thresholds = weighted ? { ...rowThresholds, threshold: weighted.value } : rowThresholds;

  const applied = applyResolvedLimit(thresholds.threshold, thresholdLimitWithoutExtra.value, thresholdFromRow.value);
  thresholdLimitWithoutExtra.value = applied.next;
  thresholdFromRow.value = applied.fromRow;

  // A row figure is reference data, not project data: the column stays empty so the API keeps reading the row.
  if (thresholds.installationalThreshold) installationThresholdLimit.value = null;
};

watch(
  () => calculationTypes.getCalculationTypes(),
  (types) => {
    // This fires whenever the list changes, which includes the country changing under the project: the
    // address decides the country, and calculation types are per-country. A type chosen for the old
    // country is then simply absent, and the fallback to Custom also clears the included phases and with
    // them three wizard steps. Say so rather than doing it in silence — resolveCalculationTypeForCountry
    // is what distinguishes "a choice was lost" from "nothing was chosen yet".
    //
    // But only judge against THE PROJECT'S list. This watcher also fires — `immediate`, on mount —
    // with whatever the provider happens to hold: nothing on a cold app, and the PREVIOUS project's
    // list when hopping between projects of different countries, since the provider refetches only
    // after localization catches up with this project's address. Resolving against those toasted
    // "not available for this country" on a project whose type was perfectly valid, pointed the form
    // at the WRONG country's Custom and re-resolved thresholds against it. Judged against the
    // project's OWN address rather than the localization store, because the localization store is
    // exactly the thing that lags here. When the project's real list lands, this watcher fires again
    // and seeds normally.
    const projectCountry = countryLongNameToEnumMap[project.value.buildingAddress?.country ?? ""];
    if (!isCalculationTypeListAuthoritative(types, projectCountry)) return;

    const resolution = resolveCalculationTypeForCountry(
      types,
      project.value.emissionCalculationAssumptions.calculationType.calculationTypeEnum,
      selectedCalculationType.value?.label ?? null
    );

    if (resolution.wasInvalidated) {
      const lost = resolution.lostLabel;
      toast.info(
        lost
          ? `${lost} is not available for this country. Switched to ${resolution.next?.label ?? "no calculation type"}.`
          : `The calculation type is not available for this country. Switched to ${
              resolution.next?.label ?? "no calculation type"
            }.`
      );
    }

    selectedCalculationType.value = resolution.next;
    // Don't do for existing projects

    if (showConstruction.value || hasBuildingTypeThresholds.value) {
      updateThresholds();
    }
    if (isExistingProject.value) {
      return;
    }
    handleNewSelectedCalculationType();
  },
  { immediate: true }
);

/**
 * Building type used to live on a step of its own, before Calculation prerequisites. Leaving that step
 * for this one remounted this component and reran the watch above for free, so a changed building type
 * always resolved a fresh threshold before anyone could see the mismatch. Now the two questions share one
 * screen (Project details' Calculation card), so nothing remounts when building type changes there, and
 * without this the written limit value would fall behind the building type actually selected, silently
 * disagreeing with ProjectTargetPanel's live "your target" figure right beside it.
 *
 * Edit is unaffected: its Calculation prerequisites step still has no building type field of its own, so
 * `project.value.buildingTypeEnum` cannot change while this component is mounted there.
 */
watch(
  () => [project.value.buildingTypeEnum, project.value.buildingUses],
  () => {
    if (showConstruction.value || hasBuildingTypeThresholds.value) {
      updateThresholds();
    }
  },
  { deep: true }
);
</script>

<template>
  <div class="flex flex-col">
    <CardHeading
      v-if="showHeading"
      class="mb-4 font-bold">
      Calculation type
    </CardHeading>

    <!-- Tighter row gap in compact mode: ProjectDetails' Calculation card needs every spare pixel to clear
         one screen without scrolling. Off by default so CalculationPrerequisites.vue, which never passes
         compact, keeps today's roomier gap. -->
    <div
      class="grid grid-cols-12 gap-x-4"
      :class="compact ? 'gap-y-4' : 'gap-y-6'">
      <div :class="['flex flex-col', fieldSpanClass]">
        <InputLabel
          label="Calculation type"
          markdown-path="calculationType"
          :required="true" />
        <SelectInputWithKeyValue
          :value="selectedCalculationType?.label"
          :options="calculationTypeOptions"
          aria-label="Calculation type"
          @update:value="handleCalculationTypeSelected" />
      </div>

      <!-- Consideration period -->
      <NumberInput
        :key="project.emissionCalculationAssumptions.calculationType.calculationTypeEnum || 0"
        v-model:value="project.emissionCalculationAssumptions.calculationType.considerationPeriod"
        :align-right="true"
        :class="fieldSpanClass"
        :label="considerationPeriodLabel"
        :disabled="selectedCalculationType?.considerationPeriod !== null"
        :error-msg="
          props.wasValidated && project.emissionCalculationAssumptions.calculationType.considerationPeriod === null
            ? 'Please enter a valid consideration period'
            : undefined
        "
        color="primary"
        tooltip="The length of time, in years, over which the building’s life cycle impacts are assessed. This period defines how long operational impacts and replacements are included in the calculation."
        :positive="true"
        :required="true" />

      <!-- Limit value -->
      <NumberInput
        v-if="project.buildingAddress.country.toUpperCase() === Country_Enum.DENMARK"
        :key="project.emissionCalculationAssumptions.calculationType.calculationTypeEnum || 0"
        v-model:value="project.emissionCalculationAssumptions.calculationType.thresholdLimitWithoutExtra"
        :align-right="true"
        :class="fieldSpanClass"
        :label="thresholdLimitLabel"
        :disabled="
          (selectedThreshold != null && selectedCalculationType?.label !== 'BR18 (2025)') ||
          (selectedCalculationType?.label === 'BR18 (2025)' && selectedThreshold !== null)
        "
        :error-msg="
          wasValidated && project.emissionCalculationAssumptions.calculationType.thresholdLimitWithoutExtra === null
            ? 'Please enter a valid limit value'
            : undefined
        "
        color="primary"
        tooltip="The limit value used to evaluate the project’s climate performance. This allows results to be compared against regulations, certification requirements, or project-specific goals."
        :positive="true"
        :required="true" />

      <NumberInput
        v-else
        :key="project.emissionCalculationAssumptions.calculationType.calculationTypeEnum || 9"
        v-model:value="project.emissionCalculationAssumptions.calculationType.thresholdLimitWithoutExtra"
        :align-right="true"
        :class="fieldSpanClass"
        :label="thresholdLimitLabel"
        :disabled="selectedThreshold != null"
        :error-msg="
          wasValidated && project.emissionCalculationAssumptions.calculationType.thresholdLimitWithoutExtra === null
            ? 'Please enter a valid limit value'
            : undefined
        "
        color="primary"
        tooltip="The limit value used to evaluate the project’s climate performance. This allows results to be compared against regulations, certification requirements, or project-specific goals."
        :positive="true"
        :required="true" />

      <!-- Construction limit: the building-type row's figure when it has one, otherwise the project's own. -->
      <NumberInput
        v-if="showConstruction"
        v-model:value="constructionLimit"
        :align-right="true"
        :class="fieldSpanClass"
        label="Construction limit (kgCO₂-eq/m²/year)"
        :disabled="selectedInstallationalThreshold != null"
        color="primary"
        :positive="true"
        markdown-path="constructionLimit" />
    </div>
  </div>
</template>
